/**
 * Role 19 — Statement of Changes in Equity — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

export function calculateTotals_610000(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
  options: {
    hasEquityColumns: boolean;
    equityColumns?: Array<{ qname: string }>;
    visibleColumns: ReadonlyArray<{
      consolidationType: 'Consolidated' | 'Separate';
      periodType: 'Current' | 'Previous';
      visible: boolean;
    }>;
  },
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  const isMFRS = xbrlPrefix === 'ifrs-full';
  const customPrefix = isMFRS ? 'ssmt-mfrs:' : 'ssmt-mpers:';
  // MPERS uses ssmt-mpers: for several change-in-equity rows in BOTH
  // modes (mirrors the component's `mpersPrefix` constant at line 1781).
  const mpersPrefix = 'ssmt-mpers:';

  const num = (key: string): number => {
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const visibleTuples = options.visibleColumns.filter((column) => column.visible);

  if (options.hasEquityColumns && options.equityColumns?.length) {
    // ── Equity-columns mode ────────────────────────────────────────
    const buildKey = (
      rowQname: string,
      columnQname: string,
      consolidationType: 'Consolidated' | 'Separate',
      periodType: 'Current' | 'Previous',
    ): string => {
      const period =
        periodType === 'Current' ? 'CurrentYear' : 'PreviousYear';
      return `${rowQname}:${columnQname}[${consolidationType}][${period}]`;
    };

    for (const tuple of visibleTuples) {
      for (const column of options.equityColumns) {
        const colQname = column.qname;
        const ct = tuple.consolidationType;
        const pt = tuple.periodType;

        // 1. EquityBalanceRestated = Equity:Beginning + ImpactOfChange...
        const equityBeginning = num(
          buildKey(`${xbrlPrefix}:Equity:Beginning`, colQname, ct, pt),
        );
        const impactChanges = num(
          buildKey(
            `${customPrefix}ImpactOfChangeInAccountingStandards`,
            colQname,
            ct,
            pt,
          ),
        );
        const equityRestated = equityBeginning + impactChanges;
        calculatedTotals[
          buildKey(`${customPrefix}EquityBalanceRestated`, colQname, ct, pt)
        ] = equityRestated;

        // 2. ComprehensiveIncome = ProfitLoss + OtherComprehensiveIncome
        const profitLoss = num(
          buildKey(`${xbrlPrefix}:ProfitLoss`, colQname, ct, pt),
        );
        const otherCI = num(
          buildKey(
            `${xbrlPrefix}:OtherComprehensiveIncome`,
            colQname,
            ct,
            pt,
          ),
        );
        const comprehensiveIncome = profitLoss + otherCI;
        calculatedTotals[
          buildKey(`${xbrlPrefix}:ComprehensiveIncome`, colQname, ct, pt)
        ] = comprehensiveIncome;

        // 3. ChangesInEquity (the big sum). MPERS uses ssmt-mpers: for
        // a handful of fields where MFRS uses ifrs-full: — see component
        // line 1781-1801 for the explicit list.
        const acquisitionDilution = num(
          buildKey(
            isMFRS
              ? `${xbrlPrefix}:IncreaseDecreaseThroughAcquisitionOfSubsidiary`
              : `${mpersPrefix}IncreaseDecreaseThroughAcquisitionOfSubsidiary`,
            colQname,
            ct,
            pt,
          ),
        );
        const iculs = num(
          buildKey(
            isMFRS
              ? `${xbrlPrefix}:IncreaseDecreaseThroughConversionOfConvertibleInstruments`
              : `${mpersPrefix}IncreaseDecreaseThroughConversionOfConvertibleInstruments`,
            colQname,
            ct,
            pt,
          ),
        );
        const issueOfEquity = num(
          buildKey(`${xbrlPrefix}:IssueOfEquity`, colQname, ct, pt),
        );
        const convertibleNotes = num(
          buildKey(
            isMFRS
              ? `${xbrlPrefix}:IssueOfConvertibleInstruments`
              : `${mpersPrefix}IssueOfConvertibleInstruments`,
            colQname,
            ct,
            pt,
          ),
        );
        const shareBasedPayment = num(
          buildKey(
            `${xbrlPrefix}:IncreaseDecreaseThroughSharebasedPaymentTransactions`,
            colQname,
            ct,
            pt,
          ),
        );
        const treasuryShares = num(
          buildKey(
            `${xbrlPrefix}:IncreaseDecreaseThroughTreasuryShareTransactions`,
            colQname,
            ct,
            pt,
          ),
        );
        const otherTransactions = num(
          buildKey(
            isMFRS
              ? `${xbrlPrefix}:IncreaseDecreaseThroughTransactionsWithOwners`
              : `${mpersPrefix}IncreaseDecreaseThroughTransactionsWithOwners`,
            colQname,
            ct,
            pt,
          ),
        );
        const otherChanges = num(
          buildKey(
            `${xbrlPrefix}:IncreaseDecreaseThroughTransfersAndOtherChangesEquity`,
            colQname,
            ct,
            pt,
          ),
        );
        const dividends = num(
          buildKey(`${xbrlPrefix}:DividendsPaid`, colQname, ct, pt),
        );

        const totalChangesInEquity =
          comprehensiveIncome +
          acquisitionDilution +
          iculs +
          issueOfEquity +
          convertibleNotes +
          shareBasedPayment +
          treasuryShares +
          otherTransactions +
          otherChanges -
          dividends;
        calculatedTotals[
          buildKey(`${xbrlPrefix}:ChangesInEquity`, colQname, ct, pt)
        ] = totalChangesInEquity;

        // 4. Equity:End = EquityBalanceRestated + ChangesInEquity
        const equityEnd = equityRestated + totalChangesInEquity;
        calculatedTotals[
          buildKey(`${xbrlPrefix}:Equity:End`, colQname, ct, pt)
        ] = equityEnd;
      }
    }
  } else {
    // ── Dual-column (legacy) mode ──────────────────────────────────
    const suffixOf = (
      consolidationType: 'Consolidated' | 'Separate',
      periodType: 'Current' | 'Previous',
    ): string => {
      const period =
        periodType === 'Current' ? 'CurrentYear' : 'PreviousYear';
      return `[${consolidationType}][${period}]`;
    };

    for (const tuple of visibleTuples) {
      const suffix = suffixOf(tuple.consolidationType, tuple.periodType);

      // 1. EquityBalanceRestated. Component lines 1826-1833: reads
      // either ifrs-full or ifrs-smes for Equity:Beginning, either
      // ssmt-mfrs or ssmt-mpers for ImpactOfChange... Writes BOTH
      // ssmt-mfrs and ssmt-mpers EquityBalanceRestated.
      const equityBeginning =
        num(`ifrs-full:Equity:Beginning${suffix}`) ||
        num(`ifrs-smes:Equity:Beginning${suffix}`);
      const impactChanges =
        num(`ssmt-mfrs:ImpactOfChangeInAccountingStandards${suffix}`) ||
        num(`ssmt-mpers:ImpactOfChangeInAccountingStandards${suffix}`);
      const equityRestated = equityBeginning + impactChanges;
      calculatedTotals[`ssmt-mfrs:EquityBalanceRestated${suffix}`] =
        equityRestated;
      calculatedTotals[`ssmt-mpers:EquityBalanceRestated${suffix}`] =
        equityRestated;

      // 2. ComprehensiveIncome.
      const profitLoss =
        num(`ifrs-full:ProfitLoss${suffix}`) ||
        num(`ifrs-smes:ProfitLoss${suffix}`);
      const otherCI =
        num(`ifrs-full:OtherComprehensiveIncome${suffix}`) ||
        num(`ifrs-smes:OtherComprehensiveIncome${suffix}`);
      const comprehensiveIncome = profitLoss + otherCI;
      calculatedTotals[`ifrs-full:ComprehensiveIncome${suffix}`] =
        comprehensiveIncome;
      calculatedTotals[`ifrs-smes:ComprehensiveIncome${suffix}`] =
        comprehensiveIncome;

      // 3. ChangesInEquity (mirrors component lines 1857-1879).
      const acquisitionDilution =
        num(
          `ifrs-full:IncreaseDecreaseThroughAcquisitionOfSubsidiary${suffix}`,
        ) ||
        num(
          `ssmt-mpers:IncreaseDecreaseThroughAcquisitionOfSubsidiary${suffix}`,
        ) ||
        0;
      const iculs =
        num(
          `ifrs-full:IncreaseDecreaseThroughConversionOfConvertibleInstruments${suffix}`,
        ) ||
        num(
          `ssmt-mpers:IncreaseDecreaseThroughConversionOfConvertibleInstruments${suffix}`,
        ) ||
        0;
      const issueOfEquity =
        num(`ifrs-full:IssueOfEquity${suffix}`) ||
        num(`ifrs-smes:IssueOfEquity${suffix}`) ||
        0;
      const convertibleNotes =
        num(`ifrs-full:IssueOfConvertibleInstruments${suffix}`) ||
        num(`ssmt-mpers:IssueOfConvertibleInstruments${suffix}`) ||
        0;
      const shareBasedPayment =
        num(
          `ifrs-full:IncreaseDecreaseThroughSharebasedPaymentTransactions${suffix}`,
        ) ||
        num(
          `ifrs-smes:IncreaseDecreaseThroughSharebasedPaymentTransactions${suffix}`,
        ) ||
        0;
      const treasuryShares =
        num(
          `ifrs-full:IncreaseDecreaseThroughTreasuryShareTransactions${suffix}`,
        ) ||
        num(
          `ifrs-smes:IncreaseDecreaseThroughTreasuryShareTransactions${suffix}`,
        ) ||
        0;
      const otherTransactions =
        num(
          `ifrs-full:IncreaseDecreaseThroughTransactionsWithOwners${suffix}`,
        ) ||
        num(
          `ssmt-mpers:IncreaseDecreaseThroughTransactionsWithOwners${suffix}`,
        ) ||
        0;
      const otherChanges =
        num(
          `ifrs-full:IncreaseDecreaseThroughTransfersAndOtherChangesEquity${suffix}`,
        ) ||
        num(
          `ifrs-smes:IncreaseDecreaseThroughTransfersAndOtherChangesEquity${suffix}`,
        ) ||
        0;
      const dividends =
        num(`ifrs-full:DividendsPaid${suffix}`) ||
        num(`ifrs-smes:DividendsPaid${suffix}`) ||
        0;

      const totalChangesInEquity =
        comprehensiveIncome +
        acquisitionDilution +
        iculs +
        issueOfEquity +
        convertibleNotes +
        shareBasedPayment +
        treasuryShares +
        otherTransactions +
        otherChanges -
        dividends;
      calculatedTotals[`ifrs-full:ChangesInEquity${suffix}`] =
        totalChangesInEquity;
      calculatedTotals[`ifrs-smes:ChangesInEquity${suffix}`] =
        totalChangesInEquity;

      // 4. Equity:End. Component line 1883 reads back EquityBalanceRestated
      // from the form (whichever prefix), then sums with totalChangesInEquity.
      const equityEnd = totalChangesInEquity + equityRestated;
      calculatedTotals[`ifrs-full:Equity:End${suffix}`] = equityEnd;
      calculatedTotals[`ifrs-smes:Equity:End${suffix}`] = equityEnd;
    }
  }

  return calculatedTotals;
}
